import { useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  useCreateReviewAndRatingMutation,
  useUpdateReviewAndRatingMutation,
} from "../../../Services/RatingAndReviewService";
import { useToast } from "../../../Helpers/Hooks/useToast";

interface ProductReviewProps {
  orderItem?: any;
  orderData?: any;
  onClose?: () => void;
}

const ProductReview = ({
  orderItem,
  orderData,
  onClose,
}: ProductReviewProps) => {
  const toast = useToast();
  const [rating, setRating] = useState(orderItem?.existingReview?.score || 0);
  const [message, setMessage] = useState(
    orderItem?.existingReview?.content || "",
  );
  const [hover, setHover] = useState(0);

  const authData: any = useSelector((state: any) => state.AuthSlice);
  const [createReviewAndRating, { isLoading, isSuccess: isCreateSuccess }] =
    useCreateReviewAndRatingMutation();
  const [
    updateReviewAndRating,
    { isLoading: updateLoading, isSuccess: isUpdateSuccess },
  ] = useUpdateReviewAndRatingMutation();

  const Star = ({
    color,
    onClick,
    onMouseEnter,
    onMouseLeave,
  }: {
    color?: string;
    onClick?: () => void;
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
  }) => {
    return (
      <svg
        className="w-6 cursor-pointer"
        viewBox="0 0 14 13"
        fill={color || "#d1d5db"} // Use `fill` directly
        xmlns="http://www.w3.org/2000/svg"
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <path d="M7 0L9.4687 3.60213L13.6574 4.83688L10.9944 8.29787L11.1145 12.6631L7 11.2L2.8855 12.6631L3.00556 8.29787L0.342604 4.83688L4.5313 3.60213L7 0Z" />
      </svg>
    );
  };

  const handleSubmit = () => {
    if (!message.trim()) {
      toast.warning("Please write your review message before submitting");
      return;
    }

    const reqBody: any = {
      productid: orderItem.productId,
      orderNumber: orderData.orderNumber,
      score: rating,
      content: message || "",
      productDenominationsku: orderItem?.productDenominationsku,
      orderItemId: orderItem?._id,
      orderValue: orderItem?.price,
      authorName:
        authData?.authUser?.firstName +
        (authData?.authUser?.lastName
          ? " " + authData?.authUser?.lastName
          : ""),
    };

    if (orderItem?.existingReview) {
      updateReviewAndRating(reqBody);
    } else {
      createReviewAndRating(reqBody);
    }
  };

  return (
    <div className="dark:bg-dark-background">
      <div className="px-4 py-6 sm:px-6">
              {!isCreateSuccess && !isUpdateSuccess && (
                <div className="p-4 max-w-md mx-auto bg-white dark:bg-dark-foreground rounded-lg">
                  <h2 className="text-lg font-semibold mb-2 text-gray-800 dark:text-white">
                    {orderItem?.existingReview
                      ? "Update Your Review"
                      : "Rate Your Experience"}
                  </h2>
                  <div className="flex gap-1 mb-4">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <Star
                        key={index}
                        color={
                          index < hover || index < rating
                            ? "#facc15"
                            : "#d1d5db"
                        }
                        onClick={() => setRating(index + 1)}
                        onMouseEnter={() => setHover(index + 1)}
                        onMouseLeave={() => setHover(rating)}
                      />
                    ))}
                  </div>
                  <textarea
                    className="w-full border border-gray-300 dark:border-gray-600 dark:bg-dark-background dark:text-white dark:placeholder-gray-500 rounded-lg p-2 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-400"
                    placeholder="Leave a message..."
                    value={message}
                    rows={6}
                    onChange={(e) => setMessage(e.target.value)}
                  ></textarea>
                  <button
                    className="mt-3 px-4 py-2 bg-yellow-500 text-white font-medium rounded-lg hover:bg-yellow-600 dark:bg-yellow-500 dark:hover:bg-yellow-600 disabled:opacity-50"
                    onClick={handleSubmit}
                    disabled={isLoading}
                  >
                    {isLoading || updateLoading
                      ? "Submitting..."
                      : orderItem?.existingReview
                        ? "Update Review"
                        : "Submit Review"}
                  </button>
                </div>
              )}

              {isCreateSuccess || isUpdateSuccess ? (
                <div
                  className="max-w-sm mx-auto bg-green-100 dark:bg-green-900/30 dark:border dark:border-green-800 p-6 rounded-xl font-[sans-serif]"
                  role="alert"
                >
                  <div className="flex items-start">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="w-10 fill-green-500 dark:fill-green-400 inline mr-4 shrink-0"
                      viewBox="0 0 23.625 23.625"
                    >
                      <path
                        d="M11.812 0C5.289 0 0 5.289 0 11.812s5.289 11.813 11.812 11.813 11.813-5.29 11.813-11.813S18.335 0 11.812 0zm2.459 18.307c-.608.24-1.092.422-1.455.548a3.838 3.838 0 0 1-1.262.189c-.736 0-1.309-.18-1.717-.539s-.611-.814-.611-1.367c0-.215.015-.435.045-.659a8.23 8.23 0 0 1 .147-.759l.761-2.688c.067-.258.125-.503.171-.731.046-.23.068-.441.068-.633 0-.342-.071-.582-.212-.717-.143-.135-.412-.201-.813-.201-.196 0-.398.029-.605.09-.205.063-.383.12-.529.176l.201-.828c.498-.203.975-.377 1.43-.521a4.225 4.225 0 0 1 1.29-.218c.731 0 1.295.178 1.692.53.395.353.594.812.594 1.376 0 .117-.014.323-.041.617a4.129 4.129 0 0 1-.152.811l-.757 2.68a7.582 7.582 0 0 0-.167.736 3.892 3.892 0 0 0-.073.626c0 .356.079.599.239.728.158.129.435.194.827.194.185 0 .392-.033.626-.097.232-.064.4-.121.506-.17l-.203.827zm-.134-10.878a1.807 1.807 0 0 1-1.275.492c-.496 0-.924-.164-1.28-.492a1.57 1.57 0 0 1-.533-1.193c0-.465.18-.865.533-1.196a1.812 1.812 0 0 1 1.28-.497c.497 0 .923.165 1.275.497.353.331.53.731.53 1.196 0 .467-.177.865-.53 1.193z"
                        data-original="#030104"
                      ></path>
                    </svg>

                    <div>
                      <h4 className="font-bold text-base mb-2 text-gray-800 dark:text-green-300">
                        Your Opinion Counts!
                      </h4>
                      <p className="block sm:inline text-sm text-gray-600 dark:text-gray-300">
                        Thanks for spending your valuable time & your rating has
                        been {orderItem?.existingReview ? "updated" : "saved"}{" "}
                        successfully.
                      </p>
                    </div>
                  </div>

                  <div className="text-right space-x-6 mt-6">
                    <Link
                      to=""
                      replace
                      className="text-blue-600 dark:text-blue-400 text-sm font-medium hover:underline"
                      onClick={onClose}
                    >
                      Ok
                    </Link>
                  </div>
                </div>
              ) : null}
      </div>
    </div>
  );
};

export default ProductReview;
